import type { CheckoutPageProps } from '@eventbrite/datalayer-library';
import { track, updatePageProps } from '@eventbrite/datalayer-library';
import { getWindowObject } from '@eventbrite/feature-detection';
import { LOCATION_CHANGE } from 'react-router-redux';
import { AnyAction, Dispatch, Store } from 'redux';
import { actionTypes } from 'redux-form';
import { HeapEventName } from '../constants/heap';
import { getIsEmbeddedInOrganizerSite } from '../selectors/app/getIsEmbeddedInOrganizerSite';
import { isExpressApplePayEnabled } from '../selectors/applePay';
import { getEventId, getIsFreeEvent } from '../selectors/event';
import {
    getIsNosSourceOfTruth,
    getOrderPersistentId,
} from '../selectors/order/typedOrderSelectors';
import { isExpressPaypalEnabled } from '../selectors/paypal';
import { getSeriesId } from '../selectors/series';
import {
    getNumberOfAddonTicketsSelected,
    getNumberOfDonationTicketsSelected,
    getNumberOfFreeTicketsSelected,
    getNumberOfPaidTicketsSelected,
    getNumberOfTicketsSelected,
} from '../selectors/tickets/selected';
import { CheckoutState } from '../state';

const getCheckoutPageProps = (
    state: CheckoutState,
    payload: any,
): CheckoutPageProps => {
    const location = getWindowObject('location');
    const destinationURL = (location?.origin || '') + (payload?.pathname || '');
    const orderId = getOrderPersistentId(state) as string | null;
    const eventId = getEventId(state);
    const isNOS = getIsNosSourceOfTruth(state);
    const isFree = getIsFreeEvent(state) as boolean | undefined;
    const seriesId = getSeriesId(state);
    const totalTicketsSelected = getNumberOfTicketsSelected(state);
    const donationTicketsSelected = getNumberOfDonationTicketsSelected(state);
    const freeTicketsSelected = getNumberOfFreeTicketsSelected(state);
    const paidTicketsSelected = getNumberOfPaidTicketsSelected(state);
    const addonTicketsSelected = getNumberOfAddonTicketsSelected(state);
    const isExpressApplePayAvailable = isExpressApplePayEnabled(state);
    const isExpressPayPalAvailable = isExpressPaypalEnabled(state);
    const isEmbeddedInOrganizerSite = getIsEmbeddedInOrganizerSite(state);

    return {
        destinationURL,
        pathname: payload?.pathname,
        routerPayload: JSON.stringify(payload || {}),
        isNOS,
        orderId,
        eventId,
        isFree,
        seriesId,
        totalTicketsSelected,
        donationTicketsSelected,
        freeTicketsSelected,
        paidTicketsSelected,
        addonTicketsSelected,
        isExpressApplePayAvailable,
        isExpressPayPalAvailable,
        isEmbeddedInOrganizerSite,
    };
};

export const dataLayerMiddleware =
    (store: Store<CheckoutState>) =>
    (next: Dispatch<CheckoutState>) =>
    (action: AnyAction) => {
        const state = store.getState();
        const { type, payload } = action;
        const location = getWindowObject('location');

        // This tracks form submits, i.e. passing from one step to another
        if (type == actionTypes.START_SUBMIT && action.meta?.form) {
            track({
                eventName: HeapEventName.submit,
                eventData: { form: action.meta.form },
            });
        }

        // This enriches all following events with all the data we're interested in
        if (
            type == LOCATION_CHANGE &&
            payload?.pathname &&
            payload.pathname !== '/' &&
            location
        ) {
            // Record Navigation Change Event w/ New Page Props.
            updatePageProps({
                page: getCheckoutPageProps(state, payload),
                eventName: HeapEventName.routerLocationChange,
            });
        }

        return next(action);
    };
